import React, { useState } from 'react';
import { FaTimes, FaFileAlt, FaCheck, FaTag } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';
import TemplateEditor from './TemplateEditor';
import ThemedSelect from '../../../common/ThemedSelect';

const CreateTemplateModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    templateName: '',
    templateDescription: '',
    templateContent: '',
    templateType: 'text',
    category: '',
    tags: '',
    isPublic: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleTemplateChange = (content, type) => {
    setFormData((prev) => ({
      ...prev,
      templateContent: content,
      templateType: type || prev.templateType,
    }));
  };

  const handleValidateTemplate = async (content, type) => {
    try {
      const response = await api.telegramBI.templates.validate(content, type);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.templateName.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!formData.templateContent.trim()) {
      toast.error('Template content is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags
          ? formData.tags
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : [],
      };
      await onSubmit(payload);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'template', label: 'Template Content' },
  ];

  const inputClass =
    'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-gray-900/60 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-surface rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
              <FaFileAlt className="text-primary-600 text-sm" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">New Template</h2>
              <p className="text-xs text-gray-500">Create a Handlebars message template</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Tabs */}
          <div className="flex gap-0 px-6 border-b border-gray-100">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div
            className={`px-6 py-5 overflow-y-auto flex-1 ${activeTab === 'template' ? 'min-h-[400px]' : ''}`}
          >
            {activeTab === 'details' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Template Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="templateName"
                    value={formData.templateName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g., Daily Summary Report"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description
                  </label>
                  <textarea
                    name="templateDescription"
                    value={formData.templateDescription}
                    onChange={handleChange}
                    rows={3}
                    className={inputClass}
                    placeholder="What does this template do?"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Template Type
                    </label>
                    <ThemedSelect
                      name="templateType"
                      value={formData.templateType}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="text">Plain Text</option>
                      <option value="markdown">Markdown</option>
                      <option value="html">HTML</option>
                    </ThemedSelect>
                    <p className="text-[11px] text-gray-400 mt-1">
                      {formData.templateType === 'text' && 'Variable substitution only'}
                      {formData.templateType === 'markdown' &&
                        'Supports **bold**, *italic*, `code`'}
                      {formData.templateType === 'html' && 'Full HTML for rich formatting'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Category
                    </label>
                    <input
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="e.g., Reports, Alerts"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <FaTag className="inline text-[10px] mr-1 text-gray-400" />
                    Tags
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="daily, summary, routes (comma-separated)"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">Separate tags with commas</p>
                </div>

                <label className="flex items-center gap-2.5 cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    name="isPublic"
                    checked={formData.isPublic}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <span className="text-sm text-gray-700 font-medium">Public template</span>
                    <p className="text-[11px] text-gray-400">Visible to all users in the system</p>
                  </div>
                </label>
              </div>
            )}

            {activeTab === 'template' && (
              <div className="h-full">
                <TemplateEditor
                  template={{
                    template_content: formData.templateContent,
                    template_type: formData.templateType,
                  }}
                  onChange={handleTemplateChange}
                  onValidate={handleValidateTemplate}
                  sidebarMode={true}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <div className="text-[11px] text-gray-400">
              {formData.templateContent.length > 0 && (
                <span>{formData.templateContent.length.toLocaleString()} characters</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  isSubmitting || !formData.templateName.trim() || !formData.templateContent.trim()
                }
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FaCheck className="text-xs" />
                    Create Template
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTemplateModal;
